'use client';
import React from 'react';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

// Hard cap matches functions/modules/coachBroadcast.js MAX_COACH_BODY.
// At send time the server appends "\n\nReply STOP to unsubscribe." (~28
// chars) to SMS sends, so 280 here = ~308 on the wire = 2 SMS segments.
// Anything over 280 will be either truncated by the server or rejected
// by Twilio — so we color-warn before send.
const MAX_BODY = 280;

// SMS segments: 160 chars per segment for plain ASCII, 70 for Unicode
// (emoji, accents). We use the conservative ASCII threshold here since
// the AI prompt is set to avoid emoji.
const SEGMENT_ASCII = 160;

/**
 * Live character + SMS-segment count for a message body. Drop-in for
 * the composer message previews so coaches see at a glance whether the
 * draft will fit in one SMS segment, two, or whether it'll get
 * server-truncated.
 *
 *   <SmsLengthIndicator body={result.bodyEn} />
 *
 * Color tier:
 *   green   — under 160 chars (1 SMS segment, cheapest)
 *   amber   — 160-280 chars (2 segments, fits but costs ~2x)
 *   red     — over 280 chars (will be truncated or rejected)
 *
 * Nothing renders if `body` is empty so it's safe to mount in skeleton
 * states without flicker.
 */
export default function SmsLengthIndicator({ body, className = '' }) {
  if (!body) return null;
  const len = body.length;
  const overCap = len > MAX_BODY;
  const twoSegments = !overCap && len > SEGMENT_ASCII;

  const tone = overCap
    ? { wrap: 'bg-red-50 text-red-700 border-red-200', label: 'Too long' }
    : twoSegments
      ? { wrap: 'bg-amber-50 text-amber-700 border-amber-200', label: '2 SMS segments' }
      : { wrap: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: '1 SMS segment' };

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tone.wrap} ${className}`}
      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
      title={overCap
        ? `${len} characters. SMS will be truncated or rejected — re-draft to keep it shorter.`
        : `${len} of ${MAX_BODY} characters · ${tone.label}`}
    >
      {len} / {MAX_BODY}
      <span className="text-[9px] opacity-70">· {tone.label}</span>
    </span>
  );
}

export { MAX_BODY };
